import './ApiVolumeTable.scss';
import { ApiVolumeTableExportButton } from '@security/ApiVolumeTableExportButton';
import { createAsyncSelectors } from '@lib/selectors';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import localeData from 'dayjs/plugin/localeData';
import { SheetComponent } from '@antv/s2-react';
import { SonarCard } from '@ui/general';
import { SonarText } from '@ui/text';
import sortAscending from 'images/sort-ascending.svg';
import sortDescending from 'images/sort-descending.svg';
import sortHover from 'images/sort-hover.svg';
import sortHoverAscending from 'images/sort-hover-ascending.svg';
import sortHoverDescending from 'images/sort-hover-descending.svg';
import sortResting from 'images/sort-resting.svg';
import updateLocale from 'dayjs/plugin/updateLocale';
import { useNavigate } from 'react-router';
import { Aggregation, EXTRA_FIELD, S2Event, TOTAL_VALUE } from '@antv/s2';
import { ApiVolumeByApplicationGroupingType, coreActions } from '@sonar-software/sonar-core';
import { ArrowLeftOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Dropdown, Space, Tooltip } from 'antd';
import { COPY_SPECIFIC, DATA_SECURITY_DASHBOARD, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { DateRangePicker, DateRangePickerTypes } from '@ui/DateRangePicker';
import { getPastDate, jsonSafeParse } from '@sonar-software/sonar-ts-lib';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useApplicationReferences, useAsyncState, useOnUnmount } from '@hooks';
dayjs.extend(updateLocale);
dayjs.extend(localeData);
const dataSecurityApiVolumeTableSelectors = createAsyncSelectors((state) => state.core.dataSecurity.apiVolume.applications.get);
function mapGroupBy(groupBy) {
    switch (groupBy) {
        case ApiVolumeByApplicationGroupingType.Day:
            return DateRangePickerTypes.Date;
        case ApiVolumeByApplicationGroupingType.Week:
            return DateRangePickerTypes.Week;
        case ApiVolumeByApplicationGroupingType.Month:
            return DateRangePickerTypes.Month;
        default:
            return DateRangePickerTypes.Date;
    }
}
const themeConfig = {
    name: 'gray',
    theme: {
        colCell: {
            bolderText: {
                fontSize: 14,
                fontWeight: 400
            },
            cell: {
                backgroundColor: '#F5F5F5'
            },
            icon: {}
        },
        cornerCell: {
            bolderText: {
                fontWeight: 400
            },
            cell: {
                backgroundColor: '#F5F5F5'
            }
        },
        dataCell: {
            cell: {
                backgroundColor: '#FFF',
                crossBackgroundColor: '#FFF',
                horizontalBorderColor: '#000000',
                horizontalBorderColorOpacity: 0.06,
                horizontalBorderWidth: 1,
                verticalBorderWidth: 0
            }
        },
        rowCell: {
            bolderText: {
                fontSize: 14
            },
            cell: {
                backgroundColor: '#FFF',
                crossBackgroundColor: '#FFF',
                horizontalBorderColor: '#000000',
                horizontalBorderColorOpacity: 0.1,
                horizontalBorderWidth: 1,
                verticalBorderWidth: 0
            },
            text: {
                fontSize: 14,
                linkTextFill: '#1677FF'
            }
        }
    }
};
const optionsBase = {
    customSVGIcons: [
        {
            name: 'sortAscending',
            svg: sortAscending
        },
        {
            name: 'sortDescending',
            svg: sortDescending
        },
        {
            name: 'sortResting',
            svg: sortResting
        },
        {
            name: 'sortHoverAscending',
            svg: sortHoverAscending
        },
        {
            name: 'sortHoverDescending',
            svg: sortHoverDescending
        },
        {
            name: 'sortHover',
            svg: sortHover
        }
    ],
    frozenFirstRow: true,
    interaction: {
        isHoveringHighlight: true,
        linkFields: (meta) => {
            var _a, _b;
            if (meta.isTotals) {
                return false;
            }
            if (meta.field === 'applicationName') {
                const { name, deletedOn } = (_b = jsonSafeParse((_a = meta.label) !== null && _a !== void 0 ? _a : '')) !== null && _b !== void 0 ? _b : {};
                return Boolean(name) && !deletedOn;
            }
            return false;
        },
        selectedCellHighlight: true,
        selectedCellsSpotlight: true
    },
    showDefaultHeaderActionIcon: false,
    style: {
        colCfg: {
            hideMeasureColumn: true
        }
    },
    totals: {
        col: {
            calcTotals: {
                aggregation: Aggregation.SUM
            },
            label: COPY_SPECIFIC.TOTAL,
            showGrandTotals: true
        },
        row: {
            calcTotals: {
                aggregation: Aggregation.SUM
            },
            label: COPY_SPECIFIC.TOTAL,
            reverseLayout: true,
            showGrandTotals: true
        }
    }
};
export const ApiVolumeTable = () => {
    var _a;
    const getDataSecurityApiVolume = useAction(coreActions.requestGetCoreDataSecurityApiVolumeByApplication);
    const apps = useApplicationReferences();
    const [dateRange, setDateRange] = useState({ start: getPastDate(7) });
    const [groupBy, setGroupBy] = useState(ApiVolumeByApplicationGroupingType.Day);
    const [sortParam, setSortParam] = useState();
    const [hoveringCell, setHoveringCell] = useState(undefined);
    const navigate = useNavigate();
    const locale = dayjs.locale();
    const originalWeekStart = dayjs().localeData()
        .firstDayOfWeek();
    const { data, isRequested } = useAsyncState(dataSecurityApiVolumeTableSelectors);
    const onMounted = useCallback((spreadSheet) => {
        spreadSheet.on(S2Event.GLOBAL_MOUSE_MOVE, () => {
            var _a, _b, _c;
            const hovering = spreadSheet.interaction.getCurrentStateName() === 'hover';
            const cell = (_a = spreadSheet === null || spreadSheet === void 0 ? void 0 : spreadSheet.interaction.getCells()) !== null && _a !== void 0 ? _a : [];
            const cellIdParts = (_c = (_b = cell[0]) === null || _b === void 0 ? void 0 : _b.id.split('[&]')) !== null && _c !== void 0 ? _c : [];
            const isHoveringCellId = cellIdParts.length > 0 ? cellIdParts[cellIdParts.length - 1] : undefined;
            if (hovering) {
                if (hoveringCell !== isHoveringCellId) {
                    setHoveringCell(isHoveringCellId);
                }
            }
            if (!hovering && hoveringCell) {
                setHoveringCell(undefined);
            }
        });
    }, [hoveringCell, setHoveringCell]);
    useEffect(() => {
        getDataSecurityApiVolume({
            calendarGroupingType: groupBy,
            end: dateRange.end,
            start: dateRange.start
        });
    }, [dateRange, getDataSecurityApiVolume, groupBy]);
    useOnUnmount(() => {
        dayjs.updateLocale(locale, {
            weekStart: originalWeekStart
        });
    });
    const handleSelectGroupBy = useCallback((value) => {
        setGroupBy(value);
        if (value === ApiVolumeByApplicationGroupingType.Week) {
            dayjs.updateLocale(locale, {
                weekStart: 1
            });
        }
        else {
            dayjs.updateLocale(locale, {
                weekStart: originalWeekStart
            });
        }
    }, [locale, originalWeekStart]);
    const groupByOptions = Object.values(ApiVolumeByApplicationGroupingType).map((item, index) => ({
        key: index,
        label: React.createElement("div", { onClick: () => handleSelectGroupBy(item) }, item)
    }));
    const tableData = (_a = useMemo(() => data === null || data === void 0 ? void 0 : data.map((app) => {
        var _a, _b;
        return (Object.assign(Object.assign({}, app), { applicationName: JSON.stringify((_a = apps[app.applicationId]) !== null && _a !== void 0 ? _a : []), date: (_b = app.date) !== null && _b !== void 0 ? _b : '' }));
    }), [apps, data])) !== null && _a !== void 0 ? _a : [];
    const dataConfig = {
        data: tableData,
        fields: {
            columns: ['date'],
            rows: ['applicationName'],
            values: ['totalEvents']
        },
        meta: [{
                field: 'applicationId'
            },
            {
                field: 'applicationName',
                formatter: useCallback((value) => {
                    var _a;
                    const { name, deletedOn, label } = (_a = jsonSafeParse(value)) !== null && _a !== void 0 ? _a : {};
                    const labelDisplay = label ? ` (${label})` : '';
                    const nameDisplay = name ? `${name}${labelDisplay}` : '-';
                    return deletedOn && name ? `${nameDisplay} (${COPY_SPECIFIC.HIDDEN})` : nameDisplay;
                }, []),
                name: COPY_SPECIFIC.APPLICATION
            },
            {
                field: 'date',
                formatter: useCallback((value) => {
                    if (groupBy === ApiVolumeByApplicationGroupingType.Month) {
                        return dayjs(value).format('MMM');
                    }
                    return dayjs(value).format('MMM DD');
                }, [groupBy])
            },
            {
                field: 'totalEvents'
            }],
        sortParams: sortParam ? [sortParam] : []
    };
    const updateSortParams = useCallback((column, direction) => {
        if (column === undefined) {
            setSortParam(undefined);
        }
        else if (column === 'Total') {
            setSortParam({
                query: {
                    [EXTRA_FIELD]: 'totalEvents'
                },
                sortByMeasure: TOTAL_VALUE,
                sortFieldId: 'applicationName',
                sortMethod: direction
            });
        }
        else {
            setSortParam({
                query: {
                    [EXTRA_FIELD]: 'totalEvents',
                    date: column
                },
                sortByMeasure: 'totalEvents',
                sortFieldId: 'applicationName',
                sortMethod: direction
            });
        }
    }, []);
    const onColCellClick = useCallback((colCellData) => {
        const query = sortParam === null || sortParam === void 0 ? void 0 : sortParam.query;
        const sortDirection = sortParam === null || sortParam === void 0 ? void 0 : sortParam.sortMethod;
        const selectedDate = colCellData.viewMeta.value;
        colCellData.viewMeta.spreadsheet.interaction.clearState();
        if ((query && !(query === null || query === void 0 ? void 0 : query.date) && selectedDate === 'Total') || (query === null || query === void 0 ? void 0 : query.date) === selectedDate) {
            if (sortDirection === 'asc') {
                updateSortParams(selectedDate, 'desc');
            }
            else {
                updateSortParams(undefined, undefined);
            }
        }
        else {
            updateSortParams(selectedDate, 'asc');
        }
    }, [sortParam, updateSortParams]);
    const displayIcon = useCallback((column, iconName, sortDirection) => {
        var _a;
        const isHoveringIcon = iconName.toLowerCase().includes('hover');
        if ((isHoveringIcon && !hoveringCell) ||
            (!sortParam && sortDirection)) {
            return false;
        }
        const showisHoveringing = isHoveringIcon && hoveringCell && column === hoveringCell;
        const showResting = !isHoveringIcon && (!hoveringCell || column !== hoveringCell);
        if (!sortParam) {
            return showisHoveringing || showResting;
        }
        const date = (_a = sortParam.query) === null || _a === void 0 ? void 0 : _a.date;
        const isMatch = date ?
            column === date :
            column === 'Total';
        if (sortDirection === sortParam.sortMethod) {
            return isMatch ? showisHoveringing || showResting : false;
        }
        return isMatch ? false : showisHoveringing || showResting;
    }, [hoveringCell, sortParam]);
    const options = Object.assign(Object.assign({}, optionsBase), { headerActionIcons: [
            {
                belongsCell: 'colCell',
                displayCondition: (meta, iconName) => displayIcon(meta.value, iconName, undefined),
                iconNames: ['sortHover', 'sortResting'],
                onClick: (params) => {
                    var _a;
                    updateSortParams((_a = params === null || params === void 0 ? void 0 : params.meta) === null || _a === void 0 ? void 0 : _a.value, 'asc');
                }
            },
            {
                belongsCell: 'colCell',
                displayCondition: (meta, iconName) => displayIcon(meta.value, iconName, 'desc'),
                iconNames: ['sortDescending', 'sortHoverDescending'],
                onClick: () => {
                    updateSortParams(undefined, undefined);
                }
            },
            {
                belongsCell: 'colCell',
                displayCondition: (meta, iconName) => displayIcon(meta.value, iconName, 'asc'),
                iconNames: ['sortAscending', 'sortHoverAscending'],
                onClick: (params) => {
                    var _a;
                    updateSortParams((_a = params === null || params === void 0 ? void 0 : params.meta) === null || _a === void 0 ? void 0 : _a.value, 'desc');
                }
            }
        ] });
    return (React.createElement(SonarCard, { extra: React.createElement(Space, { style: { gap: 16 } },
            React.createElement("div", { style: {
                    display: 'flex',
                    gap: 4
                } },
                React.createElement(SonarText, null, COPY_SPECIFIC.GROUP_BY),
                React.createElement(Dropdown, { menu: { items: groupByOptions }, trigger: ['click'] },
                    React.createElement(Space, null,
                        React.createElement(SonarText, null, groupBy),
                        React.createElement(DownOutlined, null)))),
            React.createElement(DateRangePicker, { endDate: dateRange.end, maxDate: dayjs(Date.now()), minDate: dayjs(Date.now()).subtract(1, 'year'), onChange: setDateRange, pickerType: mapGroupBy(groupBy), startDate: dateRange.start }),
            React.createElement(ApiVolumeTableExportButton, { request: {
                    calendarGroupingType: groupBy,
                    end: dateRange.end,
                    start: dateRange.start
                } })), id: 'api_volume_table', loading: isRequested, title: React.createElement(Space, { size: 'small' },
            React.createElement(Link, { to: FULL_ROUTES.DATA_SECURITY },
                React.createElement(Button, { className: 'back-to-overview-button', icon: React.createElement(ArrowLeftOutlined, null), type: 'link' })),
            React.createElement(SonarText, { className: 'h2' }, DATA_SECURITY_DASHBOARD.API_VOLUME),
            React.createElement(Tooltip, { className: 'tooltip', placement: 'bottom', title: COPY_SPECIFIC.READ_REQUESTS_ONLY },
                React.createElement(InfoCircleOutlined, null))), noSpacing: true },
        React.createElement(SheetComponent, { adaptive: {
                getContainer: () => document.getElementById('api_volume_table'),
                height: true,
                width: true
            }, dataCfg: dataConfig, onColCellClick: onColCellClick, onLinkFieldJump: (jumpData) => {
                navigate(`${FULL_ROUTES.DATA_SECURITY}/${jumpData.record.applicationId}`);
            }, onMounted: onMounted, options: options, themeCfg: themeConfig })));
};
